import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useLocalStorage } from '@vueuse/core'

export type PageBuilderExperienceMode = 'simple' | 'advanced'
const SMART_RECOMMENDATIONS_STORAGE_KEY = 'pageBuilderSmartCoachRecommendationsByScope'

export interface SmartCoachRecommendation {
  id: string
  title: string
  why: string
  change: string
  impact?: string
  risk: string
  undo: string
}

function smartCoachScopeKey(storeId: string | null | undefined, page: string | null | undefined) {
  return `${storeId?.trim() || 'global'}:${page?.trim() || 'home'}`
}

function cloneRecommendations(recommendations: SmartCoachRecommendation[]) {
  return recommendations.map(recommendation => ({ ...recommendation }))
}

function readStoredRecommendations() {
  try {
    const raw = globalThis.localStorage?.getItem(SMART_RECOMMENDATIONS_STORAGE_KEY)
    if (!raw) return {}

    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }

    return parsed as Record<string, SmartCoachRecommendation[]>
  } catch {
    return {}
  }
}

function writeStoredRecommendations(value: Record<string, SmartCoachRecommendation[]>) {
  try {
    globalThis.localStorage?.setItem(SMART_RECOMMENDATIONS_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Local persistence should never block the builder itself.
  }
}

export const usePageBuilderUxStore = defineStore('pageBuilderUx', () => {
  const experienceMode = useLocalStorage<PageBuilderExperienceMode>('pageBuilderExperienceMode', 'simple')
  const largeText = useLocalStorage('pageBuilderAccessibilityLargeText', false)
  const highContrast = useLocalStorage('pageBuilderAccessibilityHighContrast', false)
  const reducedMotion = useLocalStorage('pageBuilderAccessibilityReducedMotion', false)
  const smartRecommendationScope = ref(smartCoachScopeKey(null, null))
  const smartRecommendationsByScope = ref<Record<string, SmartCoachRecommendation[]>>(readStoredRecommendations())
  const smartCoachLoading = ref(false)
  const smartCoachError = ref('')
  const smartRecommendations = ref<SmartCoachRecommendation[]>([])

  const isSimpleMode = computed(() => experienceMode.value === 'simple')

  const accessibilityClasses = computed(() => ({
    'page-builder--large-text': largeText.value,
    'page-builder--high-contrast': highContrast.value,
    'page-builder--reduced-motion': reducedMotion.value,
  }))

  function setExperienceMode(mode: PageBuilderExperienceMode) {
    experienceMode.value = mode
  }

  function setSmartCoachScope(storeId: string | null | undefined, page: string | null | undefined) {
    const currentScope = smartRecommendationScope.value
    const nextScope = smartCoachScopeKey(storeId, page)
    const canCarryGlobalRecommendations =
      currentScope === smartCoachScopeKey(null, page)
      && nextScope !== currentScope
      && smartRecommendations.value.length > 0
      && !smartRecommendationsByScope.value[nextScope]?.length
    const recommendationsToCarry = canCarryGlobalRecommendations
      ? cloneRecommendations(smartRecommendations.value)
      : []

    smartRecommendationScope.value = nextScope
    smartRecommendations.value = cloneRecommendations(
      smartRecommendationsByScope.value[nextScope] ?? recommendationsToCarry,
    )
    smartCoachError.value = ''

    if (recommendationsToCarry.length) {
      persistSmartRecommendations()
    }
  }

  function startSmartCoachLoading() {
    smartCoachLoading.value = true
    smartCoachError.value = ''
    smartRecommendations.value = []
  }

  function stopSmartCoachLoading() {
    smartCoachLoading.value = false
  }

  function failSmartCoach(message: string) {
    smartCoachLoading.value = false
    smartCoachError.value = message
  }

  function setSmartRecommendationsFromText(text: string) {
    const parsedRecommendations = parseSmartCoachRecommendations(text)
    smartCoachLoading.value = false

    if (parsedRecommendations.length) {
      smartRecommendations.value = parsedRecommendations
      smartCoachError.value = ''
      persistSmartRecommendations()
      return
    }

    smartRecommendations.value = cloneRecommendations(
      smartRecommendationsByScope.value[smartRecommendationScope.value] ?? [],
    )
    smartCoachError.value = text.trim()
  }

  function dismissSmartRecommendation(id: string) {
    smartRecommendations.value = smartRecommendations.value.filter(recommendation => recommendation.id !== id)
    persistSmartRecommendations()
  }

  function clearSmartRecommendations() {
    smartRecommendations.value = []
    persistSmartRecommendations()
  }

  function persistSmartRecommendations() {
    const scope = smartRecommendationScope.value
    const next = { ...smartRecommendationsByScope.value }
    if (smartRecommendations.value.length) {
      next[scope] = cloneRecommendations(smartRecommendations.value)
    } else {
      delete next[scope]
    }
    smartRecommendationsByScope.value = next
    writeStoredRecommendations(next)
  }

  return {
    experienceMode,
    isSimpleMode,
    largeText,
    highContrast,
    reducedMotion,
    smartCoachLoading,
    smartCoachError,
    smartRecommendations,
    accessibilityClasses,
    setExperienceMode,
    setSmartCoachScope,
    startSmartCoachLoading,
    stopSmartCoachLoading,
    failSmartCoach,
    setSmartRecommendationsFromText,
    dismissSmartRecommendation,
    clearSmartRecommendations,
  }
})

function parseSmartCoachRecommendations(text: string): SmartCoachRecommendation[] {
  const trimmed = text.trim()
  if (!trimmed) return []

  const structured = parseStructuredRecommendations(trimmed)
  if (structured.length) return structured

  const blocks = trimmed
    .split(/(?=(?:^|\n)\s*(?:\d+[\).\-\s]*)?(?:التوصية|Recommendation)\s*[:：])/gi)
    .map(block => block.trim())
    .filter(block => /(?:التوصية|Recommendation)\s*[:：]/i.test(block))

  const sourceBlocks = blocks.length ? blocks : [trimmed]

  return sourceBlocks
    .slice(0, 5)
    .map((block, index) => recommendationFromBlock(block, index))
    .filter((recommendation): recommendation is SmartCoachRecommendation => Boolean(recommendation?.title))
}

function parseStructuredRecommendations(text: string): SmartCoachRecommendation[] {
  const jsonText = extractJsonText(text)
  if (!jsonText) return []

  try {
    const parsed = JSON.parse(jsonText) as {
      recommendations?: Array<Record<string, unknown>>
    } | Array<Record<string, unknown>>
    const records = Array.isArray(parsed) ? parsed : parsed.recommendations
    if (!Array.isArray(records)) return []

    return records.slice(0, 5).map((record, index) => {
      const title = stringField(record, ['title', 'recommendation', 'label'])
      const why = stringField(record, ['why', 'why_this_helps', 'benefit'])
      const change = stringField(record, ['change', 'what_will_change', 'what_changes'])
      const impact = stringField(record, ['impact', 'impact_label', 'action_impact'])
      const risk = stringField(record, ['risk', 'risk_level'])
      const undo = stringField(record, ['undo', 'undo_available', 'undo_availability'])

      if (!title) return null

      return normalizeRecommendation({
        title,
        why,
        change,
        impact,
        risk,
        undo,
      }, index)
    }).filter((recommendation): recommendation is SmartCoachRecommendation => Boolean(recommendation))
  } catch {
    return []
  }
}

function extractJsonText(text: string) {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i)?.[1]?.trim()
  if (fenced) return fenced

  const start = text.search(/[\[{]/)
  if (start === -1) return ''

  return text.slice(start).trim()
}

function recommendationFromBlock(block: string, index: number): SmartCoachRecommendation | null {
  const labels = [
    'التوصية',
    'Recommendation',
    'لماذا تفيد',
    'Why this helps',
    'Why',
    'ما الذي سيتغير',
    'What will change',
    'Change',
    'نوع التغيير',
    'Action impact',
    'Impact',
    'مستوى المخاطرة',
    'Risk level',
    'Risk',
    'إمكانية التراجع',
    'Undo availability',
    'Undo',
  ]
  const title = extractLabel(block, ['التوصية', 'Recommendation'], labels) || firstUsefulLine(block)
  if (!title) return null

  return normalizeRecommendation({
    title,
    why: extractLabel(block, ['لماذا تفيد', 'Why this helps', 'Why'], labels),
    change: extractLabel(block, ['ما الذي سيتغير', 'What will change', 'Change'], labels),
    impact: extractLabel(block, ['نوع التغيير', 'Action impact', 'Impact'], labels),
    risk: extractLabel(block, ['مستوى المخاطرة', 'Risk level', 'Risk'], labels),
    undo: extractLabel(block, ['إمكانية التراجع', 'Undo availability', 'Undo'], labels),
  }, index)
}

function extractLabel(block: string, targetLabels: string[], allLabels: string[]) {
  const target = targetLabels.map(escapeRegex).join('|')
  const next = allLabels.map(escapeRegex).join('|')
  const match = block.match(new RegExp(`(?:${target})\\s*[:：]\\s*([\\s\\S]*?)(?=\\n\\s*(?:${next})\\s*[:：]|$)`, 'i'))

  return cleanValue(match?.[1] ?? '')
}

function firstUsefulLine(block: string) {
  return cleanValue(block.split(/\r?\n/).find(line => line.trim() && !line.includes('بالتأكيد')) ?? '')
}

function normalizeRecommendation(
  value: Pick<SmartCoachRecommendation, 'title'> & Partial<Omit<SmartCoachRecommendation, 'id' | 'title'>>,
  index: number,
): SmartCoachRecommendation {
  const title = cleanValue(value.title)

  return {
    id: `smart-coach-${index}-${slugify(title)}`,
    title,
    why: cleanValue(value.why ?? ''),
    change: cleanValue(value.change ?? ''),
    impact: cleanValue(value.impact ?? '') || undefined,
    risk: cleanValue(value.risk ?? ''),
    undo: cleanValue(value.undo ?? ''),
  }
}

function stringField(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }

  return ''
}

function cleanValue(value: string) {
  return value
    .replace(/^[-*\s]+/, '')
    .replace(/\s+/g, ' ')
    .replace(/[.،؛:：]\s*$/, '')
    .trim()
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 36) || 'recommendation'
}

function escapeRegex(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
